import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageSquareText, Send, X, Sparkles, Loader2, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'sv_copilot_session';

/**
 * Floating Copilot-Widget — unten rechts.
 * Nutzt Emergent LLM (Claude Sonnet 4.6) via /api/copilot/chat.
 * Session-Persistierung ueber localStorage + Backend-Speicher.
 */
export const CopilotWidget = ({ userDisplayName = null }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(SESSION_KEY) || null; } catch { return null; }
  });
  const scrollRef = useRef(null);

  // Verlauf beim Oeffnen laden
  useEffect(() => {
    if (open && sessionId && messages.length === 0) {
      (async () => {
        try {
          const res = await apiFetch(`${API}/api/copilot/history?session_id=${encodeURIComponent(sessionId)}`);
          const data = await res.json();
          if (data.success && Array.isArray(data.messages)) {
            setMessages(data.messages);
          }
        } catch { /* silent */ }
      })();
    }
    // Absichtlich nur an open/sessionId gebunden — messages-length wuerde Loop erzeugen
  }, [open, sessionId, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const txt = input.trim();
    if (!txt || sending) return;
    setInput('');
    setSending(true);
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: txt, ts: new Date().toISOString() },
      { role: 'assistant', content: '', ts: new Date().toISOString(), streaming: true },
    ]);

    // SSE-Stream via apiFetch (nimmt CSRF-Header + Cookies mit)
    try {
      const body = { message: txt };
      if (sessionId) body.session_id = sessionId;
      const res = await apiFetch(`${API}/api/copilot/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok || !res.body) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Stream nicht verfuegbar (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let full = '';
      let newSessionId = null;
      let hadError = false;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE-Events sind durch \n\n getrennt
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const line = part.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (evt.type === 'session') {
            newSessionId = evt.session_id;
          } else if (evt.type === 'delta') {
            full += evt.content || '';
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant') {
                copy[copy.length - 1] = { ...last, content: full, streaming: true };
              }
              return copy;
            });
          } else if (evt.type === 'error') {
            hadError = true;
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                role: 'assistant',
                content: `⚠️ ${evt.message || 'KI-Fehler'}`,
                ts: new Date().toISOString(),
                error: true,
              };
              return copy;
            });
          } else if (evt.type === 'done') {
            setMessages((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant') {
                copy[copy.length - 1] = { ...last, content: evt.reply || full, streaming: false };
              }
              return copy;
            });
          }
        }
      }

      if (newSessionId && newSessionId !== sessionId) {
        setSessionId(newSessionId);
        try { localStorage.setItem(SESSION_KEY, newSessionId); } catch { /* ignore */ }
      }
      if (!hadError && !full) {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: 'assistant',
            content: '⚠️ Leere Antwort — bitte erneut versuchen.',
            ts: new Date().toISOString(),
            error: true,
          };
          return copy;
        });
      }
    } catch (err) {
      setMessages((prev) => {
        const copy = [...prev];
        // Ersetze den Platzhalter durch die Fehlermeldung
        copy[copy.length - 1] = {
          role: 'assistant',
          content: `⚠️ Copilot nicht erreichbar (${err.message || 'unbekannt'}). Bitte kurz erneut.`,
          ts: new Date().toISOString(),
          error: true,
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const resetSession = async () => {
    if (sessionId) {
      try {
        await apiFetch(`${API}/api/copilot/session/${sessionId}`, { method: 'DELETE' });
      } catch { /* ignore */ }
    }
    setMessages([]);
    setSessionId(null);
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Formatiere Assistant-Antworten (sehr leichte Markdown-Unterstuetzung)
  const renderContent = (text, isAssistant) => {
    if (!isAssistant) return <p className="whitespace-pre-wrap">{text}</p>;
    // Fett + Zeilenumbrueche + einfache Ueberschriften
    const html = String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/^### (.+)$/gm, '<h4 style="color:#facc15;font-weight:600;margin-top:8px;">$1</h4>')
      .replace(/^## (.+)$/gm, '<h3 style="color:#fde68a;font-weight:700;margin-top:10px;">$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#fff;">$1</strong>')
      .replace(/\n/g, '<br/>');
    return <div className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 group flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold shadow-lg shadow-yellow-500/30 hover:shadow-yellow-500/50 hover:scale-105 transition-all"
          data-testid="copilot-open-btn"
          aria-label="SmartVolt Copilot fragen"
        >
          <Sparkles className="w-5 h-5" />
          <span className="hidden sm:inline">SmartVolt Copilot fragen</span>
          <span className="sm:hidden">Copilot</span>
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-pulse ring-2 ring-black" />
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-50 w-[min(96vw,420px)] h-[min(80vh,640px)] bg-gray-950 border border-yellow-500/30 rounded-2xl shadow-2xl shadow-yellow-500/10 flex flex-col overflow-hidden"
          data-testid="copilot-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-black">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-black">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">SmartVolt Copilot</p>
                <p className="text-[10px] text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online · Baustellen-KI
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={resetSession} className="h-8 w-8 p-0 text-gray-400 hover:text-white" title="Neuer Chat" data-testid="copilot-reset-btn">
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-8 w-8 p-0 text-gray-400 hover:text-white" data-testid="copilot-close-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Nachrichten */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-500/20 to-amber-500/10 flex items-center justify-center mx-auto mb-3">
                  <MessageSquareText className="w-7 h-7 text-yellow-400" />
                </div>
                <p className="text-white font-semibold mb-1">
                  Hi{userDisplayName ? ` ${userDisplayName}` : ''} 👋
                </p>
                <p className="text-xs text-gray-400 max-w-xs mx-auto mb-5">
                  Ich bin dein Baustellen-Assistent für VDE-Normen, DGUV-Vorschriften und Arbeitssicherheit. Frag mich alles.
                </p>
                <div className="flex flex-col gap-1.5 max-w-xs mx-auto">
                  {[
                    'Grenzwert Isolationswiderstand SK I nach DIN VDE 0701-0702?',
                    'Welcher RCD-Typ ist bei einer Wallbox Pflicht?',
                    'Wie oft muss ein Baustromverteiler geprüft werden?',
                    '5 Sicherheitsregeln auf einer Baustelle?',
                  ].map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(s)}
                      className="text-left text-xs text-yellow-300 hover:text-yellow-200 border border-yellow-500/20 hover:border-yellow-500/50 bg-yellow-500/5 rounded-lg px-3 py-2 transition-colors"
                      data-testid={`copilot-suggest-${i}`}
                    >
                      → {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                    m.role === 'user'
                      ? 'bg-yellow-500 text-black rounded-br-sm'
                      : m.error
                        ? 'bg-rose-500/10 border border-rose-500/40 text-rose-200 rounded-bl-sm'
                        : 'bg-gray-900 text-gray-100 border border-gray-800 rounded-bl-sm'
                  }`}
                  data-testid={`copilot-msg-${m.role}`}
                >
                  {m.role === 'assistant' && !m.content && m.streaming ? (
                    <span className="inline-flex gap-1 items-center text-gray-400 text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
                      <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" style={{ animationDelay: '300ms' }} />
                    </span>
                  ) : (
                    <>
                      {renderContent(m.content, m.role === 'assistant')}
                      {m.streaming && m.role === 'assistant' && (
                        <span className="inline-block w-1.5 h-4 bg-yellow-400 ml-0.5 align-middle animate-pulse" />
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            {/* Kein separater "denkt nach"-Block mehr — Streaming zeigt sofort Text */}
          </div>

          {/* Input */}
          <div className="border-t border-gray-800 bg-black p-3">
            <div className="flex items-end gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Frag den Copilot… (Enter zum Senden)"
                className="bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                disabled={sending}
                data-testid="copilot-input"
              />
              <Button
                onClick={send}
                disabled={sending || !input.trim()}
                className="bg-yellow-500 hover:bg-yellow-400 text-black shrink-0"
                data-testid="copilot-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-gray-500 text-center mt-2">
              KI-Antworten koennen Fehler enthalten — bitte kritisch pruefen.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default CopilotWidget;
